/**
 * TemplatesManager component for managing notification templates
 * Table design matching NotificationAuditLog style
 * Now with detail view for creating/editing templates
 */

import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, FileText, Mail, MessageSquare, Code, X, Calendar, Trash2, AlertCircle, Eye, Smartphone, Monitor, Search, Filter, ChevronDown, Loader2 } from 'lucide-react';
import {
  NotificationTemplate,
  NotificationChannel,
  getTemplates,
  deleteTemplate,
  getChannelLabel,
  createTemplate,
} from '../../utils/notificationsBroadcastApi';
import { TemplateEditView } from './TemplateEditView';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import { generateEmailHtml } from './builder/htmlEmailGenerator';
import { EmailDocument } from './builder/types';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';

interface TemplatesManagerProps {
  onSelectTemplate?: (template: NotificationTemplate) => void;
}

export interface TemplatesManagerHandle {
  openCreate: () => void;
}

// Sample preview data - follows model_field naming pattern
const SAMPLE_PREVIEW_DATA: Record<string, string> = {
  // Practice info
  practice_name: 'Happy Paws Veterinary',
  practice_phone: '(555) 123-4567',
  practice_email: 'info@happypawsvet.com',
  practice_address: '123 Main Street, Anytown, CA 94000',
  // Pet info
  pet_name: 'Max',
  pet_names: 'Max',
  // Grammar helpers for templates
  'has_have': 'has',
  'is/are': 'is',
  'is_are': 'is',
  // Appointment info
  next_appointment_date: 'Friday, January 3rd at 2:00 PM',
  appointment_date: 'Friday, January 3rd at 2:00 PM',  // Backwards compatibility
  appointment_time: '2:00 PM',
  // Form/link info
  form_link: 'https://helppet.ai/forms/example',
  booking_link: 'https://helppet.ai/book/example',
  book_appointment_link: 'https://helppet.ai/book/example',
  // Pet owner info - new naming pattern
  pet_owner_name: 'Jane Doe',
  pet_owner_first_name: 'Jane',
  pet_owner_email: 'jane.doe@email.com',
  pet_owner_phone: '(555) 987-6543',
  // Legacy aliases (for backwards compatibility)
  owner_name: 'Jane Doe',
  owner_pet_name: 'Max',  // Combined owner + pet reference
  name: 'Jane Doe',
  first_name: 'Jane',
  email: 'jane.doe@email.com',
  phone: '(555) 987-6543',
  // Vaccine-specific (single row)
  vaccine_name: 'Rabies',
  vaccine_names: 'Rabies, Distemper',
  due_date: 'January 15, 2026',
  scheduled_date: 'December 20, 2025',
  days_overdue: '27',
};

// Sample vaccine data for loop expansion
const SAMPLE_VACCINES = [
  { pet_name: 'Max', vaccine: 'Rabies', scheduled_date: 'Dec 20, 2025', days_overdue: '27' },
  { pet_name: 'Max', vaccine: 'Distemper', scheduled_date: 'Dec 15, 2025', days_overdue: '32' },
  { pet_name: 'Bella', vaccine: 'Bordetella', scheduled_date: 'Jan 2, 2026', days_overdue: '14' },
];

// Expand vaccine loop blocks in templates for preview
const expandVaccineLoops = (text: string): string => {
  const loopRegex = /\{\{#(overdue_vaccines|scheduled_vaccines|all_vaccines)\}\}([\s\S]*?)\{\{\/\1\}\}/g;
  
  return text.replace(loopRegex, (_match, _listType: string, rowTemplate: string) => {
    // Generate sample rows from the template
    return SAMPLE_VACCINES.map(vaccine => {
      return rowTemplate
        .replace(/\{\{#is_overdue\}\}/g, '')
        .replace(/\{\{\/is_overdue\}\}/g, '')
        .replace(/\{\{pet_name\}\}/gi, vaccine.pet_name)
        .replace(/\{\{vaccine\}\}/gi, vaccine.vaccine)
        .replace(/\{\{vaccine_name\}\}/gi, vaccine.vaccine)
        .replace(/\{\{scheduled_date\}\}/gi, vaccine.scheduled_date)
        .replace(/\{\{days_overdue\}\}/gi, vaccine.days_overdue);
    }).join('');
  });
};

// Escape special regex characters in a string
const escapeRegExp = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&');
};

// Apply placeholder substitutions
const applyPlaceholders = (text: string, data: Record<string, string>): string => {
  // First expand any vaccine loops
  let result = expandVaccineLoops(text);
  
  // Then replace simple variables
  for (const [key, value] of Object.entries(data)) {
    const escapedKey = escapeRegExp(key);
    result = result.replace(new RegExp(`\\{\\{${escapedKey}\\}\\}`, 'g'), value);
    result = result.replace(new RegExp(`\\{${escapedKey}\\}`, 'g'), value);
  }
  return result;
};

// Template Preview Modal Component
interface TemplatePreviewModalProps {
  template: NotificationTemplate | null;
  onClose: () => void;
  practiceName: string;
}

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({ template, onClose, practiceName }) => {
  const [viewMode, setViewMode] = useState<'mobile' | 'desktop'>('mobile');
  
  if (!template) return null;

  // Merge sample data with actual practice name
  const previewData = { 
    ...SAMPLE_PREVIEW_DATA, 
    practice_name: practiceName,
    // Also add common variations
    practiceName: practiceName,
  };
  const subjectLine = applyPlaceholders(template.subject || 'Message from ' + practiceName, previewData);
  const bodyContent = applyPlaceholders(template.body, previewData);
  const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const initials = practiceName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  
  // Generate HTML if template has builder_document
  let emailHtml: string | null = null;
  if (template.channel === 'email_html' && template.builder_document) {
    emailHtml = generateEmailHtml(template.builder_document as unknown as EmailDocument, { practiceName });
    emailHtml = applyPlaceholders(emailHtml, previewData);
  }

  // iPhone Frame for mobile view
  const IPhoneFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="relative mx-auto" style={{ width: '280px' }}>
      <div className="relative rounded-[3rem] p-2 shadow-2xl" style={{ backgroundColor: '#1f2937' }}>
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-7 rounded-full z-20" style={{ backgroundColor: '#000' }} />
        <div className="relative rounded-[2.5rem] overflow-hidden" style={{ backgroundColor: '#000' }}>
          <div className="h-8" style={{ backgroundColor: '#fff' }} />
          <div className="px-6 pt-1 pb-1 flex items-center justify-between text-xs font-medium" style={{ backgroundColor: '#fff', color: '#111' }}>
            <span className="w-12">9:41</span>
            <div className="flex-1" />
            <div className="flex items-center gap-1 w-14 justify-end">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
              </svg>
              <div className="flex items-center">
                <div className="w-6 h-3 border border-current rounded-sm relative">
                  <div className="absolute inset-0.5 rounded-[1px]" style={{ backgroundColor: '#22c55e', width: '70%' }} />
                </div>
              </div>
            </div>
          </div>
          <div style={{ backgroundColor: '#fff', minHeight: '400px', maxHeight: '400px', overflowY: 'auto' }}>
            {children}
          </div>
          <div className="px-4 py-4" style={{ backgroundColor: '#fff' }}>
            <div className="w-32 h-1 rounded-full mx-auto" style={{ backgroundColor: '#1f2937' }} />
          </div>
        </div>
      </div>
    </div>
  );

  // Mobile Email Content
  const MobileEmailContent = () => (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#fff' }}>
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid #f3f4f6' }}>
        <svg className="w-5 h-5" style={{ color: '#4b5563' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <div className="flex items-center gap-3">
          <svg className="w-4 h-4" style={{ color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
          <svg className="w-4 h-4" style={{ color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          <svg className="w-4 h-4" style={{ color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
      </div>
      <div className="px-3 py-2" style={{ borderBottom: '1px solid #f3f4f6' }}>
        <div className="flex items-center gap-2">
          <h3 className="text-base font-normal flex-1 truncate" style={{ color: '#111827' }}>{subjectLine}</h3>
          <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#e5e7eb', color: '#4b5563' }}>Inbox</span>
        </div>
      </div>
      <div className="px-3 py-2 text-left" style={{ borderBottom: '1px solid #f3f4f6' }}>
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, #60a5fa, #2563eb)' }}>
            <span className="text-xs font-bold" style={{ color: '#fff' }}>{initials}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-left" style={{ color: '#111827' }}>{practiceName} <span className="text-xs font-normal" style={{ color: '#6b7280' }}>{dateStr}</span></p>
            <p className="text-xs text-left" style={{ color: '#6b7280' }}>to me ▾</p>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 text-left" style={{ backgroundColor: '#fff', colorScheme: 'light only', color: '#111827' }}>
        {emailHtml ? (
          <div className="overflow-hidden text-left" style={{ transform: 'scale(0.42)', transformOrigin: 'top left', width: '238%', colorScheme: 'light only', pointerEvents: 'none' }} dangerouslySetInnerHTML={{ __html: emailHtml }} />
        ) : (
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-left" style={{ color: '#374151' }}>{bodyContent}</p>
        )}
      </div>
    </div>
  );

  // Mobile SMS Content
  const MobileSMSContent = () => (
    <div className="p-4 pt-6 h-full" style={{ background: 'linear-gradient(to bottom, #f9fafb, #ffffff)' }}>
      <p className="text-xs text-center mb-4" style={{ color: '#9ca3af' }}>Today</p>
      <div className="flex justify-start mb-2">
        <div className="max-w-[85%]">
          <div className="rounded-2xl rounded-bl-md px-4 py-2.5 text-sm leading-relaxed" style={{ backgroundColor: '#e5e7eb', color: '#111827' }}>
            {bodyContent}
          </div>
          <p className="text-[10px] mt-1 ml-2" style={{ color: '#9ca3af' }}>
            {new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · {bodyContent.length} chars
          </p>
        </div>
      </div>
    </div>
  );

  // Desktop Email Preview
  const DesktopEmailPreview = () => (
    <div className="w-full max-w-3xl mx-auto rounded-lg shadow-xl overflow-hidden" style={{ backgroundColor: '#fff' }}>
      <div className="px-4 py-3 flex items-center gap-3" style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ef4444' }} />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f59e0b' }} />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#22c55e' }} />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-4 py-1 rounded-md text-xs" style={{ backgroundColor: '#e5e7eb', color: '#6b7280' }}>
            mail.google.com
          </div>
        </div>
      </div>
      <div className="px-6 py-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="flex items-center gap-4 mb-3">
          <div className="p-2 rounded-full" style={{ color: '#6b7280' }}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </div>
          <h2 className="text-xl font-normal flex-1" style={{ color: '#111827' }}>{subjectLine}</h2>
          <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: '#e5e7eb', color: '#4b5563' }}>Inbox</span>
        </div>
        <div className="flex items-start gap-3 ml-12">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, #60a5fa, #2563eb)' }}>
            <span className="text-sm font-bold" style={{ color: '#fff' }}>{initials}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span className="font-medium" style={{ color: '#111827' }}>{practiceName}</span>
              <span className="text-sm" style={{ color: '#6b7280' }}>&lt;noreply@{practiceName.toLowerCase().replace(/\s+/g, '')}.com&gt;</span>
            </div>
            <p className="text-sm" style={{ color: '#6b7280' }}>to me</p>
          </div>
          <span className="text-sm" style={{ color: '#6b7280' }}>{dateStr}</span>
        </div>
      </div>
      <div className="p-6" style={{ backgroundColor: '#fff', colorScheme: 'light only', minHeight: '400px', maxHeight: '500px', overflowY: 'auto' }}>
        {emailHtml ? (
          <div className="text-left" style={{ colorScheme: 'light only', pointerEvents: 'none' }} dangerouslySetInnerHTML={{ __html: emailHtml }} />
        ) : (
          <p className="text-base leading-relaxed whitespace-pre-wrap" style={{ color: '#374151' }}>{bodyContent}</p>
        )}
      </div>
    </div>
  );

  // Desktop SMS Preview
  const DesktopSMSPreview = () => (
    <div className="max-w-md mx-auto p-4 rounded-lg shadow-md" style={{ backgroundColor: '#fff' }}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#d1d5db' }}>
          <span className="text-sm font-semibold" style={{ color: '#4b5563' }}>{initials}</span>
        </div>
        <div className="flex-1">
          <p className="font-medium text-sm mb-1" style={{ color: '#111827' }}>{practiceName}</p>
          <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>{bodyContent}</p>
          <p className="text-xs mt-2" style={{ color: '#9ca3af' }}>{bodyContent.length} characters</p>
        </div>
      </div>
    </div>
  );

  const isEmail = template.channel === 'email_html' || template.channel === 'email_plain';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }} onClick={onClose} />
        <div className="relative z-10 w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden bg-white dark:bg-dark-bg-secondary">
          <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200 dark:border-dark-border">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Preview: {template.name}</h3>
            <div className="flex items-center gap-1 p-1 rounded-lg bg-gray-100 dark:bg-dark-bg-tertiary">
              <button
                onClick={() => setViewMode('mobile')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'mobile' 
                    ? 'bg-white dark:bg-dark-bg-secondary shadow-sm text-gray-900 dark:text-dark-text-primary' 
                    : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary'
                }`}
              >
                <Smartphone className="w-4 h-4" />
                Mobile
              </button>
              <button
                onClick={() => setViewMode('desktop')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'desktop' 
                    ? 'bg-white dark:bg-dark-bg-secondary shadow-sm text-gray-900 dark:text-dark-text-primary' 
                    : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary'
                }`}
              >
                <Monitor className="w-4 h-4" />
                Desktop
              </button>
            </div>
            <button onClick={onClose} className="p-2 rounded-full transition-colors text-gray-500 dark:text-dark-text-muted hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 bg-gray-50 dark:bg-dark-bg-primary" style={{ minHeight: '500px' }}>
            {viewMode === 'mobile' ? (
              <div className="flex flex-col items-center">
                <div className="mb-3 flex items-center gap-2 text-gray-500 dark:text-dark-text-muted">
                  {isEmail ? <Mail className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                  <span className="text-sm font-medium">{isEmail ? 'Email' : 'SMS'}</span>
                </div>
                <IPhoneFrame>
                  {isEmail ? <MobileEmailContent /> : <MobileSMSContent />}
                </IPhoneFrame>
              </div>
            ) : (
              <div>
                <div className="mb-3 flex items-center gap-2 text-gray-500 dark:text-dark-text-muted">
                  {isEmail ? <Mail className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                  <span className="text-sm font-medium">{isEmail ? 'Email Preview' : 'SMS Preview'}</span>
                </div>
                {isEmail ? <DesktopEmailPreview /> : <DesktopSMSPreview />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const TemplatesManager = forwardRef<TemplatesManagerHandle, TemplatesManagerProps>(
  ({ onSelectTemplate }, ref) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [practiceName, setPracticeName] = useState('Your Practice');
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [channelFilter, setChannelFilter] = useState<NotificationChannel | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Preview modal state
  const [previewTemplate, setPreviewTemplate] = useState<NotificationTemplate | null>(null);
  
  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<NotificationTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);


  // Filter templates based on search and channel filter
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      // Search filter - search by name
      const matchesSearch = !searchTerm.trim() || 
        template.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Channel filter
      const matchesChannel = channelFilter === 'all' || template.channel === channelFilter;
      
      return matchesSearch && matchesChannel;
    });
  }, [templates, searchTerm, channelFilter]);

  // Fetch practice name
  useEffect(() => {
    const fetchPractice = async () => {
      if (!user?.practice_id) return;
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/api/v1/practices/${user.practice_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const practice = await response.json();
          setPracticeName(practice.name || 'Your Practice');
        }
      } catch (err) {
        console.error('Failed to fetch practice:', err);
      }
    };
    fetchPractice();
  }, [user?.practice_id]);

  const loadData = async () => {
    try {
      setLoading(true);
        const templatesData = await getTemplates();
      setTemplates(templatesData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

    const openCreateView = () => {
      navigate('/dashboard/templates/new');
    };

    // Expose openCreate to parent via ref
    useImperativeHandle(ref, () => ({
      openCreate: openCreateView
    }));

    const handleSelectTemplate = (template: NotificationTemplate) => {
      // For HTML templates, go directly to visual builder
      if (template.channel === 'email_html') {
        navigate(`/dashboard/templates/${template.id}?mode=visual`);
      } else {
        navigate(`/dashboard/templates/${template.id}`);
      }
    };

    const handleBackToList = () => {
      // Use navigate for proper browser history
      navigate('/dashboard/templates');
      loadData(); // Refresh the list
  };

    const handleDeleteClick = (e: React.MouseEvent, template: NotificationTemplate) => {
      e.stopPropagation(); // Prevent row click
      setTemplateToDelete(template);
      setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
      if (!templateToDelete) return;
      
      setIsDeleting(true);
      try {
        await deleteTemplate(templateToDelete.id);
        setDeleteModalOpen(false);
        setTemplateToDelete(null);
        await loadData();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete template');
      } finally {
        setIsDeleting(false);
      }
    };

  const getChannelIcon = (channel: NotificationChannel) => {
    switch (channel) {
      case 'sms':
        return <MessageSquare className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case 'email_html':
        return <Code className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
      case 'email_plain':
        return <Mail className="w-4 h-4 text-orange-600 dark:text-orange-400" />;
      default:
        return <Mail className="w-4 h-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getChannelBadgeStyle = (channel: NotificationChannel) => {
    switch (channel) {
      case 'sms':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'email_html':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'email_plain':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

  return (
      <div className="h-full flex flex-col">

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
        </button>
      </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {/* Search and Filter Bar */}
          {!loading && templates.length > 0 && (
            <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-tertiary">
              <div className="flex items-center gap-3">
                {/* Search Input */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Channel Filter Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      channelFilter !== 'all'
                        ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-400'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-dark-bg-secondary dark:border-dark-border dark:text-dark-text-primary dark:hover:bg-dark-bg-hover'
                    }`}
                  >
                    <Filter className="w-4 h-4" />
                    {channelFilter === 'all' ? 'All Channels' : getChannelLabel(channelFilter)}
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {showFilters && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-bg-secondary rounded-lg shadow-lg border border-gray-200 dark:border-dark-border z-10">
                      <div className="py-1">
                        <button
                          onClick={() => { setChannelFilter('all'); setShowFilters(false); }}
                          className={`w-full px-4 py-2 text-left text-sm ${
                            channelFilter === 'all' 
                              ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                              : 'text-gray-700 hover:bg-gray-100 dark:text-dark-text-primary dark:hover:bg-dark-bg-hover'
                          }`}
                        >
                          All Channels
                        </button>
                        <button
                          onClick={() => { setChannelFilter('sms'); setShowFilters(false); }}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                            channelFilter === 'sms' 
                              ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                              : 'text-gray-700 hover:bg-gray-100 dark:text-dark-text-primary dark:hover:bg-dark-bg-hover'
                          }`}
                        >
                          <MessageSquare className="w-4 h-4 text-green-600" />
                          SMS
                        </button>
                        <button
                          onClick={() => { setChannelFilter('email_plain'); setShowFilters(false); }}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                            channelFilter === 'email_plain' 
                              ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' 
                              : 'text-gray-700 hover:bg-gray-100 dark:text-dark-text-primary dark:hover:bg-dark-bg-hover'
                          }`}
                        >
                          <Mail className="w-4 h-4 text-orange-600" />
                          Plain Email
                        </button>
                        <button
                          onClick={() => { setChannelFilter('email_html'); setShowFilters(false); }}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                            channelFilter === 'email_html' 
                              ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                              : 'text-gray-700 hover:bg-gray-100 dark:text-dark-text-primary dark:hover:bg-dark-bg-hover'
                          }`}
                        >
                          <Code className="w-4 h-4 text-blue-600" />
                          HTML Email
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Clear Filters */}
                {(searchTerm || channelFilter !== 'all') && (
                  <button
                    onClick={() => { setSearchTerm(''); setChannelFilter('all'); }}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-dark-text-muted dark:hover:text-dark-text-primary"
                  >
                    Clear filters
                  </button>
                )}

                {/* Results count */}
                <span className="text-sm text-gray-500 dark:text-dark-text-muted ml-auto">
                  {filteredTemplates.length} of {templates.length} templates
                </span>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-dark-text-muted">
              <FileText className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-lg font-medium">No templates yet</p>
              <p className="text-sm mb-4">Create reusable message templates to save time</p>
          <button
                onClick={openCreateView}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Create Template
          </button>
        </div>
      ) : filteredTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-dark-text-muted">
              <Search className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-lg font-medium">No templates found</p>
              <p className="text-sm mb-4">Try adjusting your search or filters</p>
              <button
                onClick={() => { setSearchTerm(''); setChannelFilter('all'); }}
                className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                Clear all filters
              </button>
            </div>
      ) : (
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-dark-bg-tertiary sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Preview
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Delete
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
          {filteredTemplates.map((template) => (
                  <tr
              key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors cursor-pointer"
            >
                    <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${
                          template.channel === 'sms' ? 'bg-green-100 dark:bg-green-900/30' :
                          template.channel === 'email_plain' ? 'bg-orange-100 dark:bg-orange-900/30' :
                          'bg-blue-100 dark:bg-blue-900/30'
                        }`}>
                          {getChannelIcon(template.channel)}
                </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getChannelBadgeStyle(template.channel)}`}>
                {getChannelLabel(template.channel)}
              </span>
            </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                        {template.name}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-600 dark:text-dark-text-secondary truncate max-w-xs">
                        {template.subject ? applyPlaceholders(template.subject, { ...SAMPLE_PREVIEW_DATA, practice_name: practiceName }) : <span className="text-gray-400">—</span>}
            </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-dark-text-secondary">
                        <Calendar className="w-3 h-3" />
                        {formatDate(template.created_at)}
                </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); setPreviewTemplate(template); }}
                        className="p-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => handleDeleteClick(e, template)}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
                    )}
                  </div>

        {/* Delete Confirmation Modal */}
        <ConfirmDeleteModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setTemplateToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          title={`Delete "${templateToDelete?.name}"?`}
          message="This template will be permanently deleted. This action cannot be undone."
          isDeleting={isDeleting}
        />

        {/* Preview Modal */}
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          practiceName={practiceName}
        />
                </div>
    );
  }
);

TemplatesManager.displayName = 'TemplatesManager';

export default TemplatesManager;
